import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatIDR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Target,
  UserX,
  Trophy,
  RotateCcw,
  LogOut,
  TrendingDown,
  Users,
  BarChart3,
  X,
} from "lucide-react";

const POLL_INTERVAL = 2000;

export default function AdminDashboard({ onLogout }) {
  const [players, setPlayers] = useState([]);
  const [state, setState] = useState({ forced_winner_id: null, auto_target_lowest: false });
  const [stats, setStats] = useState(null);

  const loadAll = useCallback(async () => {
    try {
      const [pRes, sRes, statsRes] = await Promise.all([
        api.get("/admin/players"),
        api.get("/admin/state"),
        api.get("/admin/stats"),
      ]);
      setPlayers(pRes.data);
      setState(sRes.data);
      setStats(statsRes.data);
    } catch (err) {
      // ignore transient errors
    }
  }, []);

  useEffect(() => {
    loadAll();
    const id = setInterval(loadAll, POLL_INTERVAL);
    return () => clearInterval(id);
  }, [loadAll]);

  const setWinner = async (playerId) => {
    try {
      await api.post("/admin/set-winner", { player_id: playerId });
      toast.success("Pemenang berikutnya diatur");
      loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal mengatur pemenang");
    }
  };

  const clearWinner = async () => {
    try {
      await api.post("/admin/set-winner", { player_id: null });
      toast.success("Target pemenang dihapus");
      loadAll();
    } catch (err) {
      toast.error("Gagal menghapus target");
    }
  };

  const toggleAutoTarget = async (enabled) => {
    try {
      await api.post("/admin/auto-target", { enabled });
      setState((prev) => ({ ...prev, auto_target_lowest: enabled }));
      toast.success(
        enabled
          ? "Auto-target aktif: pemain saldo terendah otomatis menang"
          : "Auto-target dimatikan",
      );
    } catch (err) {
      toast.error("Gagal mengubah auto-target");
    }
  };

  const kickPlayer = async (playerId, name) => {
    try {
      await api.post(`/admin/kick/${playerId}`);
      toast.success(`${name} dikeluarkan`);
      loadAll();
    } catch (err) {
      toast.error("Gagal mengeluarkan pemain");
    }
  };

  const resetSession = async () => {
    try {
      await api.post("/admin/reset");
      toast.success("Sesi direset");
      loadAll();
    } catch (err) {
      toast.error("Gagal reset sesi");
    }
  };

  const activePlayers = players.filter((p) => p.is_active);
  const lowestActive = activePlayers.length
    ? activePlayers.reduce((min, p) => (p.balance < min.balance ? p : min), activePlayers[0])
    : null;

  return (
    <div className="admin-theme">
      <div className="max-w-7xl mx-auto p-4 sm:p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <p className="text-xs uppercase tracking-widest text-slate-500 mono">
              Admin Control Room
            </p>
            <h1 className="text-2xl font-semibold text-slate-900">Sesi Simulasi Judol</h1>
          </div>
          <Button
            data-testid="admin-logout-button"
            variant="outline"
            onClick={onLogout}
            className="border-slate-300"
          >
            <LogOut className="w-4 h-4 mr-1.5" />
            Logout
          </Button>
        </div>

        {/* Stats cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="admin-card p-4">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500 mb-2">
              <Users className="w-3.5 h-3.5" />
              Peserta Aktif
            </div>
            <p className="text-3xl font-semibold mono text-slate-900" data-testid="stat-active-players">
              {activePlayers.length}
            </p>
          </div>
          <div className="admin-card p-4">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500 mb-2">
              <BarChart3 className="w-3.5 h-3.5" />
              Total Putaran
            </div>
            <p className="text-3xl font-semibold mono text-slate-900" data-testid="stat-total-spins">
              {stats?.total_spins ?? 0}
            </p>
          </div>
          <div className="admin-card p-4">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500 mb-2">
              <TrendingDown className="w-3.5 h-3.5" />
              Net "Untung" Bandar
            </div>
            <p className="text-3xl font-semibold mono text-emerald-600" data-testid="stat-house-profit">
              {formatIDR(stats?.house_net_profit ?? 0)}
            </p>
          </div>
          <div className="admin-card p-4">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500 mb-2">
              <Trophy className="w-3.5 h-3.5" />
              Total Taruhan
            </div>
            <p className="text-3xl font-semibold mono text-slate-900" data-testid="stat-total-bet">
              {formatIDR(stats?.total_bet ?? 0)}
            </p>
          </div>
        </div>

        {/* Controls */}
        <div className="admin-card p-5 mb-6">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-700 mb-4">
            Kontrol Manipulasi
          </h2>

          <div className="grid sm:grid-cols-2 gap-4">
            {/* Auto-target switch */}
            <div className="flex items-center justify-between p-3 rounded-md border border-slate-200 bg-slate-50">
              <div className="flex items-start gap-3">
                <Target className="w-4 h-4 text-slate-700 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-slate-900">Auto-target Saldo Terendah</p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Pemain dengan saldo terendah otomatis menang (pancing terus main)
                  </p>
                </div>
              </div>
              <Switch
                data-testid="auto-target-switch"
                checked={state.auto_target_lowest}
                onCheckedChange={toggleAutoTarget}
              />
            </div>

            {/* Current forced winner */}
            <div className="flex items-center justify-between p-3 rounded-md border border-slate-200 bg-slate-50">
              <div className="flex items-start gap-3">
                <Trophy className="w-4 h-4 text-amber-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-slate-900">Pemenang Berikutnya (Manual)</p>
                  <p className="text-xs text-slate-500 mt-0.5 mono" data-testid="forced-winner-name">
                    {state.forced_winner_id
                      ? players.find((p) => p.id === state.forced_winner_id)?.name ||
                        "Pemain dihapus"
                      : "— Tidak ada —"}
                  </p>
                </div>
              </div>
              {state.forced_winner_id && (
                <Button
                  data-testid="clear-winner-button"
                  variant="ghost"
                  size="sm"
                  onClick={clearWinner}
                  className="text-slate-600 hover:text-red-600"
                >
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>

          {state.auto_target_lowest && lowestActive && (
            <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-md text-xs text-amber-900">
              <span className="font-semibold">Target saat ini:</span>{" "}
              <span className="mono">{lowestActive.name}</span> dengan saldo{" "}
              <span className="mono">{formatIDR(lowestActive.balance)}</span> akan menang
              di putaran berikutnya.
            </div>
          )}
        </div>

        {/* Players table */}
        <div className="admin-card overflow-hidden mb-6">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-700">
              Peserta Real-time
            </h2>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  data-testid="reset-session-button"
                  variant="outline"
                  size="sm"
                  className="border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                >
                  <RotateCcw className="w-4 h-4 mr-1.5" />
                  Reset Sesi
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Reset seluruh sesi?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Semua peserta akan dikeluarkan dan riwayat permainan terhapus. Aksi ini
                    tidak bisa dibatalkan.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Batal</AlertDialogCancel>
                  <AlertDialogAction
                    data-testid="reset-confirm-button"
                    onClick={resetSession}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    Reset Sekarang
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>

          <div className="overflow-x-auto">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Nama</th>
                  <th>Saldo</th>
                  <th>Rugi</th>
                  <th>Spin</th>
                  <th>Menang</th>
                  <th>Status</th>
                  <th className="text-right">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {players.length === 0 && (
                  <tr>
                    <td colSpan={7} className="text-center text-slate-400 py-8">
                      Belum ada peserta. Minta peserta untuk join melalui halaman utama.
                    </td>
                  </tr>
                )}
                {players.map((p) => {
                  const loss = (p.initial_balance || 5_000_000) - p.balance;
                  const isForced = state.forced_winner_id === p.id;
                  return (
                    <tr
                      key={p.id}
                      data-testid={`player-row-${p.name}`}
                      className={isForced ? "forced-winner" : ""}
                    >
                      <td className="font-medium text-slate-900">
                        {p.name}
                        {isForced && (
                          <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-amber-200 text-amber-900 mono">
                            NEXT WIN
                          </span>
                        )}
                      </td>
                      <td className="mono">{formatIDR(p.balance)}</td>
                      <td className={`mono ${loss > 0 ? "text-red-600" : "text-slate-400"}`}>
                        {loss > 0 ? `-${formatIDR(loss)}` : "—"}
                      </td>
                      <td className="mono text-slate-700">{p.spins_count || 0}</td>
                      <td className="mono text-slate-700">{p.wins_count || 0}</td>
                      <td>
                        {p.is_active ? (
                          <span className="inline-flex items-center gap-1 text-xs">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                            Aktif
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs text-slate-400">
                            <span className="w-1.5 h-1.5 rounded-full bg-slate-300" />
                            Dikeluarkan
                          </span>
                        )}
                      </td>
                      <td className="text-right space-x-1.5">
                        {p.is_active && (
                          <>
                            <Button
                              data-testid={`force-win-${p.name}`}
                              size="sm"
                              variant="outline"
                              onClick={() => setWinner(p.id)}
                              disabled={isForced}
                              className="h-8 text-xs border-amber-300 text-amber-700 hover:bg-amber-50"
                            >
                              <Trophy className="w-3 h-3 mr-1" />
                              Force Win
                            </Button>
                            <Button
                              data-testid={`kick-${p.name}`}
                              size="sm"
                              variant="outline"
                              onClick={() => kickPlayer(p.id, p.name)}
                              className="h-8 text-xs border-red-300 text-red-600 hover:bg-red-50"
                            >
                              <UserX className="w-3 h-3 mr-1" />
                              Kick
                            </Button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Educational reveal CTA */}
        {stats && stats.total_spins > 0 && (
          <div className="admin-card p-5 bg-slate-900 text-white border-slate-900">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-amber-400 mb-2">
              Ringkasan untuk Edukasi
            </h3>
            <p className="text-sm text-slate-300 leading-relaxed">
              {stats.total_players} peserta sudah memutar slot{" "}
              <span className="mono text-white">{stats.total_spins}</span> kali, mempertaruhkan
              total <span className="mono text-white">{formatIDR(stats.total_bet)}</span>.
              "Bandar" mengantongi keuntungan bersih{" "}
              <span className="mono text-emerald-400">{formatIDR(stats.house_net_profit)}</span>.
              {stats.biggest_loser && (
                <>
                  {" "}Peserta dengan kerugian terbesar:{" "}
                  <span className="text-white font-semibold">{stats.biggest_loser.name}</span>{" "}
                  (<span className="mono text-red-400">-{formatIDR(stats.biggest_loser.loss)}</span>).
                </>
              )}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
